import React from 'react';

/**
 * Inline SVG icon set.
 *
 * Replaces the emoji previously used in navigation, headings, category
 * labels and the weather widget. Emoji render inconsistently across
 * platforms and sit awkwardly next to a serif display face; these are
 * stroke icons that inherit `currentColor` and the surrounding font size.
 *
 * Every icon is decorative - callers provide the accessible text - so each
 * one is marked `aria-hidden` and kept out of the accessibility tree.
 */

const base = (size) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.6,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': 'true',
  focusable: 'false',
});

/* --- Interface ---------------------------------------------------------- */

export const IconSearch = ({ size = 16 }) => (
  <svg {...base(size)}>
    <circle cx="11" cy="11" r="7" />
    <path d="m20 20-3.6-3.6" />
  </svg>
);

export const IconClose = ({ size = 16 }) => (
  <svg {...base(size)}>
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

export const IconArrowLeft = ({ size = 16 }) => (
  <svg {...base(size)}>
    <path d="M19 12H5" />
    <path d="m11 18-6-6 6-6" />
  </svg>
);

export const IconArrowRight = ({ size = 16 }) => (
  <svg {...base(size)}>
    <path d="M5 12h14" />
    <path d="m13 6 6 6-6 6" />
  </svg>
);

export const IconMenu = ({ size = 16 }) => (
  <svg {...base(size)}>
    <path d="M4 7h16M4 12h16M4 17h16" />
  </svg>
);

export const IconLeaf = ({ size = 32 }) => (
  <svg {...base(size)}>
    <path d="M11 20A7 7 0 0 1 18 4h3v3a7 7 0 0 1-7 7h-3" />
    <path d="M11 20v-9" />
    <path d="M11 14 7.5 10.5" />
  </svg>
);

/* --- Monthly task sections --------------------------------------------- */

export const IconSeed = ({ size = 15 }) => (
  <svg {...base(size)}>
    <path d="M12 21v-7" />
    <path d="M12 14c-3.5 0-6-2.5-6-6 3.5 0 6 2.5 6 6Z" />
    <path d="M12 14c3.5 0 6-2.5 6-6-3.5 0-6 2.5-6 6Z" />
  </svg>
);

export const IconTrowel = ({ size = 15 }) => (
  <svg {...base(size)}>
    <path d="M14.5 3.5 20 9l-2.2 2.2" />
    <path d="M17.8 11.2 9 20a3 3 0 0 1-4.2-4.2l8.8-8.8" />
  </svg>
);

export const IconShears = ({ size = 15 }) => (
  <svg {...base(size)}>
    <circle cx="6" cy="18" r="2.5" />
    <circle cx="18" cy="18" r="2.5" />
    <path d="M7.7 16.3 17 4M16.3 16.3 7 4" />
  </svg>
);

export const IconBasket = ({ size = 15 }) => (
  <svg {...base(size)}>
    <path d="M3 9h18l-1.6 9a2 2 0 0 1-2 1.7H6.6a2 2 0 0 1-2-1.7Z" />
    <path d="m8 9 2.5-5M16 9l-2.5-5" />
  </svg>
);

export const IconEye = ({ size = 15 }) => (
  <svg {...base(size)}>
    <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
    <circle cx="12" cy="12" r="2.5" />
  </svg>
);

/* --- Weather (WMO code groups) ----------------------------------------- */

export const IconSun = ({ size = 20 }) => (
  <svg {...base(size)}>
    <circle cx="12" cy="12" r="4.2" />
    <path d="M12 2.5v2M12 19.5v2M2.5 12h2M19.5 12h2M5.3 5.3l1.4 1.4M17.3 17.3l1.4 1.4M18.7 5.3l-1.4 1.4M6.7 17.3l-1.4 1.4" />
  </svg>
);

export const IconSunCloud = ({ size = 20 }) => (
  <svg {...base(size)}>
    <path d="M8 6.5a3.5 3.5 0 0 1 6.3-.6" />
    <path d="M5.5 4.2V2.8M2.6 7h1.4M8.4 7h1.4M3.5 5l1 1M7.5 5l-1 1" />
    <path d="M7 19h10a3.2 3.2 0 0 0 .3-6.4A4.6 4.6 0 0 0 8.4 12 3.5 3.5 0 0 0 7 19Z" />
  </svg>
);

export const IconCloud = ({ size = 20 }) => (
  <svg {...base(size)}>
    <path d="M7 18h10a3.3 3.3 0 0 0 .3-6.6A4.8 4.8 0 0 0 8.3 10.8 3.6 3.6 0 0 0 7 18Z" />
  </svg>
);

export const IconFog = ({ size = 20 }) => (
  <svg {...base(size)}>
    <path d="M7 13h10a3.2 3.2 0 0 0 .3-6.4A4.6 4.6 0 0 0 8.4 6 3.5 3.5 0 0 0 7 13Z" />
    <path d="M4 17h16M6.5 20.5h11" />
  </svg>
);

export const IconDrizzle = ({ size = 20 }) => (
  <svg {...base(size)}>
    <path d="M7 14h10a3.2 3.2 0 0 0 .3-6.4A4.6 4.6 0 0 0 8.4 7 3.5 3.5 0 0 0 7 14Z" />
    <path d="M9 17.5v1.5M12.5 17.5v1.5M16 17.5v1.5" />
  </svg>
);

export const IconRain = ({ size = 20 }) => (
  <svg {...base(size)}>
    <path d="M7 13.5h10a3.2 3.2 0 0 0 .3-6.4A4.6 4.6 0 0 0 8.4 6.5 3.5 3.5 0 0 0 7 13.5Z" />
    <path d="M8.5 16.5 7.5 20M12.5 16.5l-1 3.5M16.5 16.5l-1 3.5" />
  </svg>
);

export const IconSnow = ({ size = 20 }) => (
  <svg {...base(size)}>
    <path d="M7 13h10a3.2 3.2 0 0 0 .3-6.4A4.6 4.6 0 0 0 8.4 6 3.5 3.5 0 0 0 7 13Z" />
    <path d="M9 17v.01M12.5 19v.01M16 17v.01M12.5 16v.01" />
  </svg>
);

export const IconStorm = ({ size = 20 }) => (
  <svg {...base(size)}>
    <path d="M7 13h10a3.2 3.2 0 0 0 .3-6.4A4.6 4.6 0 0 0 8.4 6 3.5 3.5 0 0 0 7 13Z" />
    <path d="M13 15.5 10 20h3l-.8 3 3.3-5h-2.8Z" />
  </svg>
);

export const IconThermometer = ({ size = 20 }) => (
  <svg {...base(size)}>
    <path d="M12 3.5a2 2 0 0 1 2 2v8.3a4 4 0 1 1-4 0V5.5a2 2 0 0 1 2-2Z" />
    <path d="M12 17.5v-4" />
  </svg>
);
